import { useEffect, useState } from "react";

import { UseFormReturn } from "react-hook-form";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { Alert } from "../../components/base/Alert";
import { Button } from "../../components/base/Button";
import { BasicInformationFormFields, EditBasicInformation } from "../../components/EditCustomer/EditBasicInformation";
import { CustomerFeatureFormFields, EditCustomerFeatureList } from "../../components/EditCustomer/EditCustomerFeatureList";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useCustomer from "../../data/Customer/useCustomer";
import useCustomerMutations from "../../data/Customer/useCustomerMutations";
import useCustomerFeaturesMutations from "../../data/CustomerFeature/useCustomerFeaturesMutations";
import { errorResolver } from "../../utils/errorResolver";
import EditCustomerBranding from "../../components/EditCustomer/EditCustomerBranding";

export function EditCustomer() {
  const { customerId } = useParams();
  const { customer } = useCustomer(customerId!);
  const { update: updateCustomer } = useCustomerMutations();
  const { update: updateCustomerFeatures } = useCustomerFeaturesMutations(customerId!);
  const { setInfo } = usePageNameContext();
  const navigate = useNavigate();

  const [basicInfoMethods, setBasicInfoMethods] = useState<UseFormReturn<BasicInformationFormFields>>();
  const [featureMethods, setFeatureMethods] = useState<UseFormReturn<CustomerFeatureFormFields>>();

  const [basicInfoSuccess, setBasicInfoSuccess] = useState(false);
  const [featureSuccess, setFeatureSuccess] = useState(false);

  const handleBasicInfoSubmit = (data: any) => {
    updateCustomer.mutate(
      { ...data, status: parseInt(data.status) },
      { onSuccess: () => setBasicInfoSuccess(true) }
    );
  };

  const handleFeatureSubmit = (data: any) => {
    const features: string[] = [];

    Object.keys(data).forEach((key) => {
      if (data[key]) {
        features.push(key);
      }
    });

    updateCustomerFeatures.mutate(
      { features },
      { onSuccess: () => setFeatureSuccess(true) }
    );
  };

  const handleSave = () => {
    basicInfoMethods?.handleSubmit(handleBasicInfoSubmit)();
    featureMethods?.handleSubmit(handleFeatureSubmit)();
  };

  const handleReset = () => {
    basicInfoMethods?.reset();
    featureMethods?.reset();
  };

  useEffect(() => {
    if (basicInfoSuccess && featureSuccess) {
      navigate(-1);
    }
  }, [basicInfoSuccess, featureSuccess, navigate]);

  useEffect(() => {
    if (!customer.isLoading && customer.data) {
      setInfo({
        name: customer.data.name,
        desc: `Edit ${customer.data.name}'s details`,
        pageTitle: `Edit Customer | ${customer.data.name}`,
      });
    } else {
      setInfo({
        name: "Edit Customer",
        desc: "Edit customer details",
        pageTitle: "Edit Customer",
      });
    }
  }, [customer.isLoading, customer.data]);

  if (!customerId) {
    return <Navigate to="/customers" />;
  } else if (customer.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <LoadingWheel />
      </div>
    );
  } else if (customer.isError || !customer.data) {
    return (
      <Alert
        severity="error"
        title="Error getting customer"
        message={errorResolver(customer.error)}
      />
    );
  } else {
    return (
      <>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <EditBasicInformation
            customer={customer.data}
            onSubmit={handleBasicInfoSubmit}
            onReady={(methods) => setBasicInfoMethods(methods)}
          />
          <div className="md:col-span-2 flex flex-col gap-4">
            <EditCustomerFeatureList
              customerId={customer.data.id}
              onSubmit={handleFeatureSubmit}
              onReady={(methods) => setFeatureMethods(methods)}
            />

            <EditCustomerBranding
              customer={customer.data}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Button onClick={handleSave}>Save</Button>
            <Button variant="outlined" onClick={handleReset}>
              Reset
            </Button>
          </div>
        </div>
      </>
    );
  }
}
